import React, { useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'

import 'styles/AppFantasia.scss'
import { useStoreActions, useStoreState } from 'store'
import RateTable from 'components/rate-table/RateTable'
import Filters from 'components/filters/FiltersContainer'
import TabbedSection from 'components/rate-table/TabbedSection'
import FiltersSummaryText from 'components/filters/FilterSummaryText'
import preampReady from 'utils/preampReady'
import HelpDeskRow from './rate-table/row/HelpDeskRow'
import BrandedRowV2 from './rate-table/row/BrandedRowV2'
import EmailCapture from './EmailCapture'
import LeftRailFilters from '././filters/LeftRailFilters'
import throttle from 'utils/throttle'

function App(): JSX.Element {
  const hasPaidOffers = useStoreState(state => state.offers.hasPaidOffers)
  const offers = useStoreState(state => state.offers.offers)
  const { isLoading } = useStoreState(state => state.offers)
  const { setDisplayTargets, setDeviceType, updateDimensions, setTabletDeviceType } = useStoreActions(
    actions => actions.dimensions
  )
  const { initialize } = useStoreActions(actions => actions.initialization)
  const setTableRect = useStoreActions(actions => actions.ui?.setTableRect)

  const updateDisplayTargets = (isDesktop: boolean) => {
    if (isDesktop) {
      if (!isLoading) updateDimensions({ displayTargets: globalThis.displayTargetsByDevice.desktop })
      setDisplayTargets(globalThis.displayTargetsByDevice.desktop)
    } else {
      if (!isLoading) updateDimensions({ displayTargets: globalThis.displayTargetsByDevice.mobile })
      setDisplayTargets(globalThis.displayTargetsByDevice.mobile)
    }
  }

  const updateDeviceTypes = (isDesktop: boolean) => {
    if (isDesktop) {
      setDeviceType('desktop')
    } else {
      setDeviceType('mobile')
    }
  }

  const updateTabletDeviceTypes = (isTablet: boolean) => {
    if (isTablet) {
      setTabletDeviceType(true)
    } else {
      setTabletDeviceType(false)
    }
  }

  const handleMediaQueryChange = (isDesktop: boolean) => {
    // isDesktop represents when the viewport is less than 89.9375em as set below
    updateDisplayTargets(isDesktop)
    updateDeviceTypes(isDesktop)
  }

  /** Classy break points
   * lg - (min-width: 89.9375em, 1440px)
   * md- (min-width: 62.4375em, 999px)
   * sm - (min-width: 37.4375em, 599px)
   */
  const isDesktop = useMediaQuery({ minWidth: '62.4375em' }, undefined, handleMediaQueryChange)
  const isTablet = useMediaQuery({ minWidth: '37.4375em', maxWidth: '62.4374em' }, undefined, updateTabletDeviceTypes)

  const flags = useStoreState(state => state.featureFlags.flags)

  useEffect(() => {
    updateDisplayTargets(isDesktop)
    updateDeviceTypes(isDesktop)
    updateTabletDeviceTypes(isTablet)
    preampReady(initialize, initialize)
    //  *do not track updateDisplayTargets changes*
    // eslint-disable-next-line
  }, [isDesktop])

  useEffect(() => {
    const scrollCheck = throttle(() => {
      const el = document.querySelector('#mortgage-rate-table-app')
      const rect = el ? el.getBoundingClientRect() : { top: 0, bottom: 0 }
      setTableRect?.(rect)
    }, 100)

    document.addEventListener('scroll', scrollCheck)

    return () => {
      document.removeEventListener('scroll', scrollCheck)
    }
  }, [setTableRect])

  const hasSituationalSponsoredOffer = offers.some(o => o.id === 'sage-situational-sponsored-offer')

  return (
    <>
      {isDesktop ? (
        <>
          <div className="SplitDisplay wrapper display-grid grid-cols-12 gap-8">
            <div className="SplitDisplay-leftRail col-span-3 pr-4 box-shadow-right" data-testid="left-rail-filters">
              <LeftRailFilters />
            </div>
            <div className="col-span-9">
              <FiltersSummaryText />
              <TabbedSection />
              {/* show RateForIt here only when there are no paid offers, incl extra offers */}
              {(!offers.length || (!hasPaidOffers && !hasSituationalSponsoredOffer)) && !isLoading ? (
                <>
                  <div className="wrapper" data-testid="no-coverage-rows">
                    <table className="w-full">
                      <tbody>
                        {flags['showHelpDesk'] && <HelpDeskRow />}
                        <BrandedRowV2 />
                      </tbody>
                    </table>
                  </div>
                  <EmailCapture />
                </>
              ) : null}
              <RateTable />
            </div>
          </div>
        </>
      ) : (
        <>
          <Filters />
          <TabbedSection />
          {/* show RateForIt here only when there are no paid offers, incl extra offers */}
          {(!offers.length || (!hasPaidOffers && !hasSituationalSponsoredOffer)) && !isLoading ? (
            <>
              <div className="wrapper" data-testid="no-coverage-rows">
                <table className="w-full">
                  <tbody>
                    {flags['showHelpDesk'] && <HelpDeskRow />}
                    <BrandedRowV2 />
                  </tbody>
                </table>
              </div>
              <EmailCapture />
            </>
          ) : null}
          <RateTable />
        </>
      )}
    </>
  )
}

export default App
